/**
 * `GET /v1/dashboard` (D07, §11) — o que a revenda produziu no período.
 *
 * ── Cada bloco declara de QUEM são os números ──────────────────────────────
 *
 * O campo `abrangencia` aparece em todo bloco, e não uma vez no topo, porque a
 * resposta é honestamente heterogênea: o funil pode ser recortado pela carteira
 * de quem pergunta, e o volume de atendimento não pode.
 *
 * O motivo é de dado, não de permissão: `read.conversations` guarda o id de
 * agente do Chatwoot, e nenhuma coluna o liga a um membership do CliqCRM.
 * Atribuir conversa a pessoa por nome ou e-mail produziria número que PARECE
 * recortado sem ser — que é exatamente a armadilha da B16 (*"o total do
 * dashboard continua contando o que o usuário não pode ver"*).
 *
 * Um `gestor` de unidade recebe, então, o funil da unidade dele e o volume de
 * atendimento da revenda inteira, com cada bloco dizendo qual é qual. É menos
 * elegante que um número só, e é a única forma de ele não tomar decisão em cima
 * de um recorte que não existe.
 *
 * ── "Fechada" no comercial, e ganho ou perdido no resultado ──────────────
 *
 * A C06 registrou que as opções de `stage` do Twenty são `{valor, rotulo,
 * posicao}` e não marcam ganho nem perda. O bloco `comercial` continua falando
 * de oportunidade que SAIU das etapas abertas.
 *
 * Desde a 057 a revenda DIZ quais etapas finais são ganho e quais são perda, e
 * o bloco `resultado` (24/09/2026) conta cada uma. A conversão sai dele, e só
 * das etapas classificadas: a final sem resultado não entra em lado nenhum, e o
 * bloco diz quantas faltam classificar, em vez de adivinhar.
 *
 * ── Não há indicador de SLA ────────────────────────────────────────────────
 *
 * `primeira_resposta_*` é o fato — quanto o cliente esperou. Cumprimento de SLA
 * é a D08 e ainda não tem regra; a A07 é explícita em não inventar indicador
 * sem regra definida.
 *
 * ── Saúde da integração vem VIVA, e não do agregado ────────────────────────
 *
 * É a única parte da tela que não pode estar atrasada quinze minutos: ela existe
 * para responder *"o lead do Webmotors parou de chegar?"*, e essa pergunta com
 * resposta de um quarto de hora atrás é a pergunta errada.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

/**
 * De quem são os números de um bloco.
 *
 * Os mesmos níveis da carteira (§13.1), e não um vocabulário próprio: quem lê
 * "unidade" aqui já sabe o que significa em toda a API.
 */
export const AbrangenciaSchema = z.enum(['tenant', 'unidade', 'time', 'propria']);
export type Abrangencia = z.infer<typeof AbrangenciaSchema>;

/** `YYYY-MM-DD` no fuso da revenda — ver `periodo.fuso`. */
const DiaSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const AtendimentoPorDiaSchema = z.object({
  dia: DiaSchema,
  /**
   * ATENDIMENTOS iniciados no dia, e não conversas (25/09/2026).
   *
   * A abertura da conversa e cada volta do cliente depois de um
   * encerramento: "cliente que volta a escrever numa conversa encerrada é um
   * novo atendimento". O nome do campo ficou para não quebrar o `/v1`.
   * `primeira_resposta_*` e `conversas_sem_resposta` são medidos por
   * atendimento também.
   */
  conversas_iniciadas: z.number().int(),
  /**
   * Quantos ENCERRAMENTOS houve no dia (D08) — cada um, e não só o último de
   * cada conversa: encerrada, reaberta e encerrada de novo são dois.
   *
   * Conta o dia do FECHAMENTO, e `conversas_iniciadas` conta o da abertura —
   * são conjuntos de dias diferentes e as duas NÃO se subtraem. Uma conversa
   * aberta segunda e resolvida quarta aparece uma vez em cada.
   *
   * O campo faltava na D07 porque a projeção não datava a mudança de status; a
   * D08 criou o carimbo e ele passou a ter fonte.
   */
  conversas_resolvidas: z.number().int(),
  mensagens_recebidas: z.number().int(),
  mensagens_enviadas: z.number().int(),
  /** Nulo quando nenhuma conversa do dia teve resposta a medir. */
  primeira_resposta_p50_segundos: z.number().int().nullable(),
  primeira_resposta_p90_segundos: z.number().int().nullable(),
  conversas_sem_resposta: z.number().int(),
});

export const AtendimentoPorCanalSchema = z.object({
  /** Canal fino da matriz da A03. `desconhecido` quando o inbox não projetou. */
  canal: z.string(),
  conversas_iniciadas: z.number().int(),
  mensagens_recebidas: z.number().int(),
  mensagens_enviadas: z.number().int(),
  primeira_resposta_p50_segundos: z.number().int().nullable(),
});

export const ComercialPorDiaSchema = z.object({
  dia: DiaSchema,
  oportunidades_criadas: z.number().int(),
  /** Saiu das etapas abertas. Não é "ganha" — ver o cabeçalho. */
  oportunidades_fechadas: z.number().int(),
  valor_fechado_micros: z.number().int(),
});

export const FunilPorEtapaSchema = z.object({
  /** O valor da etapa, como a oportunidade o guarda (`F_POSVENDA_NOVO`). */
  etapa: z.string(),
  /**
   * O funil da etapa, o nome dela e se é final (25/09/2026).
   *
   * O bloco trazia só o valor cru, e a tela o mostrava: `ETAPA_DO_MEIO`,
   * `F_POSVENDA_TESTE`, e duas "Novo" de funis diferentes na mesma tabela sem
   * como distinguir. As linhas vêm na ordem do quadro — funil a funil, e
   * dentro de cada um a ordem das etapas.
   */
  funil: z.string(),
  funil_nome: z.string(),
  /** O nome que a revenda deu; o valor cru quando a etapa saiu do retrato. */
  nome: z.string(),
  /**
   * Fora das etapas abertas: a venda já saiu do funil ali. "Em aberto" não
   * se aplica, e a tela não a soma como tal.
   */
  final: z.boolean(),
  /** Fluxo: quantas ENTRARAM na etapa no período. */
  entradas: z.number().int(),
  /**
   * Foto: quantas ESTAVAM na etapa no fim do último dia com foto.
   *
   * Nulo quando o job não rodou naquele dia — `read.opportunities` só guarda o
   * estado de agora, e reconstruir a foto de um dia passado exigiria replay da
   * timeline. Nulo diz "não sei"; zero diria "não havia nenhuma".
   */
  em_aberto: z.number().int().nullable(),
  valor_em_aberto_micros: z.number().int().nullable(),
});

/**
 * Ganhas e perdidas de um funil no período (057).
 *
 * Contadas pela ENTRADA na etapa final dentro do período, pela linha do tempo:
 * a oportunidade ganha em agosto não conta em setembro. A conversão é
 * `ganhas / (ganhas + perdidas)`, e a tela a calcula: com os dois números na
 * resposta, quem lê confere a conta.
 */
export const ResultadoDoFunilSchema = z.object({
  funil: z.string(),
  nome: z.string(),
  ganhas: z.number().int(),
  perdidas: z.number().int(),
  /** O valor das ganhas, como está AGORA na oportunidade. */
  valor_ganho_micros: z.number().int(),
});

/** Quantas perdas do período citaram cada motivo (058). `null` é sem motivo. */
export const MotivoDePerdaNoPeriodoSchema = z.object({
  motivo: z.string().nullable(),
  quantidade: z.number().int(),
});

/**
 * Saúde de uma conexão com engine ou canal.
 *
 * A D07 pede isto *"no dashboard de produto, não só no técnico"*: quem percebe
 * que o lead do Webmotors parou de chegar é o gestor comercial, não a
 * infraestrutura.
 */
export const SaudeDaIntegracaoSchema = z.object({
  provider: z.string(),
  kind: z.string(),
  status: z.enum(['ativo', 'degradado', 'pausado', 'erro', 'desativado']),
  /** Quando a descoberta de schema rodou (C04). Nulo = nunca. */
  schema_sincronizado_em: InstanteSchema.nullable(),
  /**
   * O último evento que CHEGOU desta origem.
   *
   * É o campo que responde a pergunta de verdade. Status `ativo` com último
   * evento de anteontem é uma integração quebrada que se declara saudável —
   * e é o modo de falha que a A07 chama de "não simular sucesso".
   */
  ultimo_evento_em: InstanteSchema.nullable(),
  /** Eventos parados em `retrying` — o acúmulo que ainda pode se resolver. */
  eventos_em_retentativa: z.number().int(),
  /** Eventos que desistiram. Não se resolvem sozinhos; alguém precisa olhar. */
  eventos_em_dlq: z.number().int(),
});

export const DashboardSchema = z.object({
  periodo: z.object({
    de: DiaSchema,
    ate: DiaSchema,
    /**
     * O fuso em que os dias foram cortados (`core.tenant_settings.timezone`).
     *
     * Vai na resposta porque sem ele a tela não sabe se "12/08" termina à
     * meia-noite de Brasília ou de UTC — e a diferença de três horas move
     * conversa de um dia para o outro.
     */
    fuso: z.string(),
  }),

  atendimento: z.object({
    abrangencia: AbrangenciaSchema,
    por_dia: z.array(AtendimentoPorDiaSchema),
    por_canal: z.array(AtendimentoPorCanalSchema),
  }),

  comercial: z.object({
    abrangencia: AbrangenciaSchema,
    por_dia: z.array(ComercialPorDiaSchema),
  }),

  funil: z.object({
    abrangencia: AbrangenciaSchema,
    por_etapa: z.array(FunilPorEtapaSchema),
  }),

  /**
   * Ganhas, perdidas e por que se perdeu (057, 058). Lido VIVO, e não do
   * agregado: o resultado das etapas acabou de nascer e não tem histórico
   * materializado. É do tenant, como o funil por etapa.
   */
  resultado: z.object({
    abrangencia: AbrangenciaSchema,
    por_funil: z.array(ResultadoDoFunilSchema),
    motivos_de_perda: z.array(MotivoDePerdaNoPeriodoSchema),
    /**
     * Etapas finais ainda sem ganho ou perdido. Maior que zero, a conversão
     * está incompleta, e a tela diz isso em vez de mostrar um número bonito.
     */
    etapas_finais_sem_resultado: z.number().int(),
  }),

  /**
   * A saúde das integrações — e ela é OPCIONAL, o que é a parte importante.
   *
   * O dashboard inteiro é liberado por `indicador × ler`, que o gestor tem. A
   * saúde das integrações não é indicador: ela é `integracao × ler`, que a
   * §13.2 dá ao administrador da organização e à operação da Cliqfy. Enquanto
   * as duas viajaram no mesmo pacote, o recurso mais restrito pegou carona na
   * permissão do mais aberto — e um bloco que a rota dedicada recusaria era
   * servido pela rota do painel.
   *
   * AUSENTE, e não lista vazia. Vazio é um fato sobre a organização: "não há
   * integração ligada aqui", que é uma informação grave e a tela diz com todas
   * as letras. Quem não pode ver precisa de uma tela onde o bloco não existe —
   * não de uma que afirma uma coisa falsa sobre a revenda.
   */
  integracoes: z.array(SaudeDaIntegracaoSchema).optional(),

  /**
   * §14.3 — quando o agregado foi calculado.
   *
   * O carimbo é o do job, não o das projeções. Um dashboard servido de
   * agregado de duas horas atrás precisa dizer isso; sem a marca, o gestor lê
   * o número da manhã achando que é o de agora.
   */
  frescor: z.object({
    stale: z.boolean(),
    projetado_em: InstanteSchema.nullable(),
  }),
});

export type Dashboard = z.infer<typeof DashboardSchema>;
export type SaudeDaIntegracao = z.infer<typeof SaudeDaIntegracaoSchema>;
